import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { cn } from '../../../utils/cn';

const TreatmentPhaseTimeline = ({ phases, onDragStart, onDragOver, onDrop, getStatusColor }) => {
  return (
    <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-4 md:p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
          <Icon name="GitBranch" size={20} className="text-primary" />
          Treatment Phases
        </h3>
        <Button variant="ghost" size="sm" iconName="Plus">
          Add Phase
        </Button>
      </div>

      <div className="space-y-4">
        {phases?.map((phase, index) => (
          <div
            key={phase?.id}
            draggable
            onDragStart={() => onDragStart(phase)}
            onDragOver={onDragOver}
            onDrop={() => onDrop(phase)}
            className="bg-background rounded-lg border border-border p-4 cursor-move hover:shadow-elevation-1 transition-smooth"
          >
            {/* Phase Header */}
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-start gap-3 flex-1">
                <div className="flex items-center justify-center w-8 h-8 bg-primary/10 rounded-full flex-shrink-0 mt-1">
                  <span className="text-sm font-semibold text-primary">{index + 1}</span>
                </div>
                <div className="flex-1">
                  <h4 className="text-base font-semibold text-foreground mb-1">{phase?.phaseName}</h4>
                  <div className="flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Icon name="Calendar" size={12} />
                      {phase?.startDate} - {phase?.endDate}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="User" size={12} />
                      {phase?.provider}
                    </span>
                    {phase?.specialty && (
                      <span className="flex items-center gap-1">
                        <Icon name="Stethoscope" size={12} />
                        {phase?.specialty}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={cn(
                  "inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border",
                  getStatusColor(phase?.status)
                )}>
                  {phase?.status}
                </span>
                <button className="p-1 hover:bg-muted rounded transition-smooth">
                  <Icon name="GripVertical" size={16} className="text-muted-foreground" />
                </button>
              </div>
            </div>

            {/* Procedures List */}
            <div className="ml-11 space-y-2">
              {phase?.procedures?.map((procedure, procIdx) => (
                <div key={procIdx} className="flex items-center justify-between p-2 bg-muted/30 rounded-lg">
                  <div className="flex items-center gap-2 flex-1">
                    <Icon name="Check" size={14} className={procedure?.status === 'completed' ? 'text-success' : 'text-muted-foreground'} />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-foreground">
                        {procedure?.code} - {procedure?.name}
                      </p>
                      {procedure?.tooth && (
                        <p className="text-xs text-muted-foreground">{procedure?.tooth}</p>
                      )}
                      {procedure?.scheduledDate && (
                        <p className="text-xs text-muted-foreground">Scheduled: {procedure?.scheduledDate}</p>
                      )}
                      {procedure?.recallInterval && (
                        <p className="text-xs text-muted-foreground">Recall: {procedure?.recallInterval}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-semibold text-foreground">${procedure?.cost}</span>
                    <span className={cn(
                      "text-xs px-2 py-1 rounded",
                      getStatusColor(procedure?.status)
                    )}>
                      {procedure?.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            {/* Phase Notes */}
            {phase?.notes && (
              <div className="ml-11 mt-3 p-3 bg-muted/50 rounded-lg">
                <p className="text-xs text-muted-foreground flex items-start gap-2">
                  <Icon name="FileText" size={12} className="mt-0.5 flex-shrink-0" />
                  <span>{phase?.notes}</span>
                </p>
              </div>
            )}

            {/* Phase Actions */}
            <div className="ml-11 mt-3 flex items-center gap-2">
              <Button variant="ghost" size="xs" iconName="Edit">
                Edit Phase
              </Button>
              <Button variant="ghost" size="xs" iconName="Plus">
                Add Procedure
              </Button>
              <Button variant="ghost" size="xs" iconName="Calendar">
                Schedule
              </Button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default TreatmentPhaseTimeline;